import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { LinkedStudy, isValidStaticStudyId } from "@shared/types";
import { isExpiredLinkedStudy, isClosedLinkedStudy } from "@shared/study";

import { useStudyView } from "@/context/ViewContexts";
import { StudyContextData } from "@/context/AppContext";

import { AppSurface } from "@shared/components/Surface";

import { T } from "@/components/i18n";
import { ExportInstructions } from "@/components/submission/ExportInstructions";
import { StudyExportHelp, StudyGenericHelp } from "@/components/help/StudyHelp";
import { Heading } from "@/components/study/Heading";

import { CircleAlert, DownloadIcon, Lightbulb } from "lucide-react";

import { motion } from "motion/react";
import { RoundButton } from "@shared/components/Button";
import { useUIContext } from "@/context/UIContext";

function WelcomeText({ study }: { study: LinkedStudy }) {
  const { t } = useTranslation();
  const studyId = study.id;
  const knownId = isValidStaticStudyId(studyId);

  return (
    <>
      <p className="text-lg mb-4 pb-4 border-b border-neutral-200 dark:border-neutral-800">
        {knownId ? (
          <T i18nKey="view.init.intro1Study" values={{ study: t(`study.${studyId}.name`) }} />
        ) : (
          <T i18nKey="view.init.intro1" />
        )}
      </p>
      <p className="text-lg mb-4">
        <T i18nKey="view.init.intro2" />
      </p>
      <div
        className="text-base px-4 py-3 border border-blue-200 dark:border-blue-800/50 rounded-xl
                   bg-blue-50/50 dark:bg-blue-800/10 text-blue-950 dark:text-blue-50"
      >
        <div className="flex items-center gap-x-1 mb-2 text-sm font-semibold">
          <Lightbulb size={16} />
          Please note
        </div>
        <p className="mb-2">
          <T i18nKey="view.init.info1" />
        </p>
        <p className="">
          <T i18nKey="view.init.info2" />
        </p>
      </div>
    </>
  );
}

export function InitView({ studyContext }: { studyContext: StudyContextData }) {
  const {
    state: studyViewState,
    setState: setStudyViewState,
    setView: setStudyView,
  } = useStudyView();
  const { study, readState } = studyContext;

  const { getAnimated, setAnimated } = useUIContext();
  const animated = getAnimated("init-view");

  useEffect(() => {
    if (study && (isClosedLinkedStudy(study) || isExpiredLinkedStudy(study))) {
      setStudyView("closed");
    }
  }, [readState]);

  if (readState.status === "error") {
    return (
      <div className="w-full max-w-2xl my-auto">
        <Heading />
        <AppSurface>
          <h2 className="text-lg font-medium flex items-center gap-x-1.5 mb-4 text-red-500">
            <CircleAlert size={20} strokeWidth={3} />
            <T i18nKey="view.init.studyError.status" />
          </h2>
          <p className="text-gray-700 dark:text-neutral-300">
            <T i18nKey="view.init.studyError.alert" />
          </p>
          <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
            <StudyGenericHelp />
          </div>
        </AppSurface>
      </div>
    );
  }

  if (readState.status === "fetching" || !study) {
    return null;
  }

  return (
    <motion.div
      initial={animated ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2, duration: 0.4 }}
      className="w-full max-w-2xl mt-12"
      onAnimationComplete={() => setAnimated("init-view")}
    >
      <Heading />
      <WelcomeText study={study} />

      <AppSurface className="mt-8">
        <h2 className="text-lg font-semibold flex items-center gap-x-1.5 mb-4">
          <DownloadIcon className="stroke-blue-500" size={20} strokeWidth={3} />
          <T i18nKey="view.init.title" />
        </h2>
        <p className="mb-4 text-sm text-neutral-600 dark:text-neutral-400 font-semibold">
          <T i18nKey="view.init.disclaimer" />
        </p>
        <ExportInstructions
          tab={studyViewState.instructionsTab}
          onChange={(tab) =>
            setStudyViewState((state) => ({
              ...state,
              instructionsTab: tab,
            }))
          }
        />
        <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-800">
          <StudyExportHelp studyId={study?.id} />
        </div>
      </AppSurface>
      <div className="mt-6 mb-16">
        <RoundButton onClick={() => setStudyView("submission")}>Next: Data import</RoundButton>
      </div>
    </motion.div>
  );
}
